import { ID, Port } from '../../types/models'
import { PortConfig } from '../../types/ports'
import { PortControl } from './PortControl'

/**
 * An appliance-like object for availability checks: raw API data carries the
 * port IDs in `ports`, instantiated `Appliance` objects in `portIDs`.
 */
export type PortOwner = { ports?: readonly ID[], portIDs?: readonly ID[] }

/**
 * IDs of every port referenced by any of the given appliances.
 */
export function referencedPortIDs(appliances: readonly PortOwner[]): Set<ID> {
  const used = new Set<ID>()
  for (const appliance of appliances) {
    for (const id of appliance.ports ?? appliance.portIDs ?? []) {
      used.add(id)
    }
  }
  return used
}

/**
 * Keep only the port controls that no appliance references — the ports that
 * are free to host a NEW appliance.
 *
 * Availability is deliberately based on appliance references, not on the
 * port's `enabled` hardware config: deleting an appliance removes its row
 * synchronously but disables its ports through an asynchronous flash that the
 * device must confirm (and never does when it is offline, or when the push
 * gives up after its retries). Judging by `enabled` therefore kept ports
 * looking occupied minutes-to-forever after their appliance was gone, and,
 * conversely, offered ports whose owning appliance keeps them disabled on
 * purpose (an optimizer control port in manual mode), inviting two appliances
 * onto one port.
 */
export function unreferencedPortControls(
  portControls: PortControl<Port, PortConfig>[],
  appliances: readonly PortOwner[],
): PortControl<Port, PortConfig>[] {
  const used = referencedPortIDs(appliances)
  return portControls.filter(ctrl => !used.has(ctrl.port.id as ID))
}
